<template>
  <ion-content class="text-selectable modal-content">
    <div class="content-wrapper">
      <div class="scrollable-content">
        <div class="info-section">
          <div class="invoice-header">
            <div class="invoice-icon">
              <font-awesome-icon :icon="['fas', 'paper-plane']" />
            </div>
            <div class="invoice-details">
              <h2 class="invoice-title">Send Invoices</h2>
              <div class="invoice-subtitle">Export & Send</div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <div class="form-card">
            <div class="date-selector">
              <div class="date-item">
                <h4>{{ t("settings-page.from-date") }}</h4>
                <div class="date-input-wrapper">
                  <ion-datetime-button datetime="datetimeFrom"></ion-datetime-button>
                </div>
              </div>
              <div class="date-item">
                <h4>{{ t("settings-page.to-date") }}</h4>
                <div class="date-input-wrapper">
                  <ion-datetime-button datetime="datetimeTo"></ion-datetime-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="action-section">
        <ion-button expand="block" size="large" class="primary-action" @click="onSend()" :disabled="sendInProgress">
          <font-awesome-icon :icon="['fas', 'paper-plane']" class="button-icon" />
          {{ t("common.send") }}
        </ion-button>
      </div>
    </div>

    <ion-modal :keep-contents-mounted="true">
      <!-- From Date -->
      <ion-datetime
          id="datetimeFrom"
          :show-default-buttons="true"
          presentation="date"
          v-model="form.startDate"
      ></ion-datetime>
    </ion-modal>
    <ion-modal :keep-contents-mounted="true">
      <!-- To Date -->
      <ion-datetime
          id="datetimeTo"
          :show-default-buttons="true"
          presentation="date"
          v-model="form.endDate"
      ></ion-datetime>
    </ion-modal>
  </ion-content>
</template>

<script lang="ts">
import { useUserTalent } from "@/hooks/talent";
import errorHandlerService from "@/services/error-handler.service";
import userService from "@/services/user.service";
import {
  IonContent,
  IonButton,
  modalController,
  IonDatetime,
  IonDatetimeButton,
  toastController,
} from "@ionic/vue";
import { DateTime } from "luxon";
import { defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "SendInvoicesModal",
  components: {
    IonContent,
    IonButton,
    IonDatetime,
    IonDatetimeButton,
  },
  setup() {
    const { t } = useI18n();
    const sendInProgress = ref<boolean>(false);
    const form = ref<{
      startDate: string;
      endDate: string;
    }>({
      startDate: DateTime.now().startOf("month").toISODate(),
      endDate: DateTime.now().endOf("month").toISODate(),
    });
    useUserTalent();

    const onSend = async () => {
      try {
        sendInProgress.value = true;

        let startDate = form.value.startDate;
        let endDate = form.value.endDate;

        if (!startDate || !DateTime.fromISO(startDate).isValid) {
          startDate = DateTime.now().startOf("month").toISODate();
        }
        if (!endDate || !DateTime.fromISO(endDate).isValid) {
          endDate = DateTime.now().endOf("month").toISODate();
        }

        await userService.sendInvoices({ startDate, endDate });

        (
            await toastController.create({
              message: "Invoices sent",
              position: "top",
              duration: 1000,
              color: "primary",
            })
        ).present();
        modalController.dismiss();
      } catch (error: any) {
        errorHandlerService.handleApiError(error);
      } finally {
        sendInProgress.value = false;
      }
    };

    return {
      t,
      form,
      sendInProgress,
      onSend,
    };
  },
});
</script>

<style scoped>
/* Content Wrapper */
.content-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 0;
}

.scrollable-content {
  flex: 1;
  padding: 10px;
}

/* Info Section */
.info-section {
  margin-bottom: 24px;
}

.invoice-header {
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid var(--loopin-color-gs-30);
}

.invoice-icon {
  width: 48px;
  height: 48px;
  margin-right: 16px;
  border-radius: 50%;
  background: var(--loopin-color-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.invoice-details {
  flex: 1;
}

.invoice-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px 0;
  line-height: 1.3;
  color: var(--loopin-color-gs-100);
}

.invoice-subtitle {
  font-size: 14px;
  color: var(--loopin-color-gs-60);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Form Section */
.form-section {
  margin-bottom: 24px;
}

.form-card {
  background: var(--loopin-item-background);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--loopin-color-gs-30);
}

.date-selector {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.date-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.date-item h4 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: var(--loopin-color-gs-90);
}

.date-input-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.date-input-wrapper ion-datetime-button {
  --background: var(--loopin-color-gs-20);
  --color: var(--loopin-color-gs-90);
  --border-radius: 8px;
  --padding: 12px 16px;
  border: 1px solid var(--loopin-color-gs-30);
}

/* Action Section */
.action-section {
  padding: 20px;
}

.primary-action {
  --background: var(--loopin-color-blue);
  --background-activated: var(--loopin-color-blue);
  --color: white;
  --border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  transition: all 0.2s ease;
}

.primary-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
}

.primary-action:disabled {
  opacity: 0.6;
  transform: none;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.button-icon {
  margin-right: 8px;
  font-size: 14px;
}

/* Modal Content */
.modal-content {
  --background: var(--loopin-color-gs-10);
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .invoice-title {
    font-size: 18px;
  }
  
  .form-card {
    padding: 16px;
  }
  
  .action-section {
    padding: 16px 10px;
  }
  
  .date-selector {
    gap: 16px;
  }
}
</style>
